import { messagingService } from '@/services/messagingService';
import { resizeImageClient } from '@/utils/resizeImageClient';
import { supabase } from '@/utils/supabase';
import { extractUrlsFromText } from '@/utils/urlUtils';
import { useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import ChatViewModular from './ChatViewModular';
import { ChatDetails, Message, Participant } from '.';

type ChatViewContainerProps = {
  userId: string;
  activeChat: string | null;
  className?: string;
  onBackButton?: () => void;
};

/**
 * Container component that handles data fetching and state management for the chat view.
 * Uses the modular ChatViewModular component for presentation.
 */
export default function ChatViewContainer({ userId, activeChat, className, onBackButton }: ChatViewContainerProps) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [chatDetails, setChatDetails] = useState<ChatDetails | null>(null);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [userPresence, setUserPresence] = useState<Record<string, { online: boolean, lastSeen?: string }>>({});

  // Fetch chat details
  useEffect(() => {
    if (!activeChat) return;

    const fetchChatDetails = async () => {
      try {
        const chat = await messagingService.fetchChat(activeChat);
        if (chat) {
          setChatDetails({
            id: chat.id,
            name: chat.name,
            type: chat.type,
            imageUrl: chat.imageUrl,
            participants: chat.participants || [],
            createdBy: chat.createdBy
          });

          // Fetch presence info for all participants
          if (chat.participants && chat.participants.length > 0) {
            // Fetch user profiles to get presence information
            const participantProfiles = await Promise.all(
              chat.participants.map(async (participantId) => {
                try {
                  const { data } = await supabase
                    .from('profiles')
                    .select('userId, online, last_seen')
                    .eq('userId', participantId)
                    .single();

                  return data;
                } catch (err) {
                  console.error('Error fetching participant profile:', err);
                  return null;
                }
              })
            );

            // Update presence state
            const presenceData: Record<string, { online: boolean, lastSeen?: string }> = {};
            participantProfiles.forEach(profile => {
              if (profile) {
                presenceData[profile.userId] = {
                  online: profile.online || false,
                  lastSeen: profile.last_seen
                };
              }
            });

            setUserPresence(presenceData);
          }
        }
      } catch (error) {
        console.error('Error fetching chat details:', error);
      }
    };

    fetchChatDetails();

    // Subscribe to chat updates
    const subscription = messagingService.subscribeToChats(userId, (payload) => {
      if (payload.new && payload.new.id === activeChat) {
        // Update chat details if this chat was updated
        console.log('Chat update received:', payload.new);

        // Fetch updated chat details to ensure all properties are correctly mapped
        fetchChatDetails();
      }
    });

    // Subscribe to user presence updates for all participants
    let presenceSubscription: { unsubscribe: () => void } | null = null;

    if (chatDetails?.participants && chatDetails.participants.length > 0) {
      presenceSubscription = messagingService.subscribeToUserPresence(
        chatDetails.participants,
        (payload) => {
          if (payload.new) {
            setUserPresence(prev => ({
              ...prev,
              [payload.new.userId]: {
                online: payload.new.online || false,
                lastSeen: payload.new.last_seen
              }
            }));
          }
        }
      );
    }

    return () => {
      subscription?.unsubscribe();
      if (presenceSubscription) {
        presenceSubscription.unsubscribe();
      }
    };
  }, [activeChat, userId, chatDetails?.participants]);

  // Fetch participants
  useEffect(() => {
    if (!chatDetails?.participants?.length) return;

    const fetchParticipants = async () => {
      try {
        const userProfiles = await messagingService.fetchUserProfiles(chatDetails.participants);

        const participantData: Participant[] = Object.values(userProfiles).map(profile => ({
          id: profile.userId,
          firstName: profile.firstName,
          lastName: profile.lastName,
          imageUrl: profile.imageUrl,
          online: profile.online || false,
          lastSeen: profile.lastSeen
        }));

        setParticipants(participantData);
      } catch (error) {
        console.error('Error fetching participants:', error);
      }
    };

    fetchParticipants();

    // Subscribe to presence updates
    const userIds = chatDetails.participants;
    const subscription = messagingService.subscribeToUserPresence(userIds, (payload) => {
      if (payload.new) {
        const updatedUser = payload.new;
        setParticipants(prev =>
          prev.map(participant => {
            if (participant.id === updatedUser.userId) {
              return {
                ...participant,
                online: updatedUser.online,
                lastSeen: updatedUser.lastSeen
              };
            }
            return participant;
          })
        );
      }
    });

    return () => {
      subscription?.unsubscribe?.();
    };
  }, [chatDetails?.participants]);

  // Fetch messages
  useEffect(() => {
    if (!activeChat) return;

    const fetchMessages = async () => {
      try {
        const messageData = await messagingService.fetchMessages(activeChat);

        const formattedMessages: Message[] = messageData.map(msg => ({
          id: msg.id,
          sender: msg.senderId,
          text: msg.text,
          timestamp: msg.timestamp,
          imageUrl: msg.imageUrl,
          linkPreview: msg.linkPreview,
          reactions: msg.reactions || {}
        }));

        setMessages(formattedMessages);
      } catch (error) {
        console.error('Error fetching messages:', error);
      }
    };

    fetchMessages();

    // Subscribe to new messages
    const subscription = messagingService.subscribeToMessages(activeChat, (payload) => {
      if (payload.new) {
        const newMsg = payload.new;

        setMessages(prev => {
          // Check if message already exists
          const exists = prev.some(msg => msg.id === newMsg.id);
          if (exists) {
            return prev.map(msg =>
              msg.id === newMsg.id ?
                {
                  id: newMsg.id,
                  sender: newMsg.sender_id,
                  text: newMsg.text,
                  timestamp: newMsg.timestamp,
                  imageUrl: newMsg.image_url,
                  linkPreview: newMsg.link_preview,
                  reactions: newMsg.reactions || {}
                } : msg
            );
          } else {
            return [
              ...prev,
              {
                id: newMsg.id,
                sender: newMsg.sender_id,
                text: newMsg.text,
                timestamp: newMsg.created_at,
                imageUrl: newMsg.image_url,
                linkPreview: newMsg.link_preview,
                reactions: newMsg.reactions || {}
              }
            ];
          }
        });
      }

      // Handle deleted messages
      if (payload.eventType === 'DELETE') {
        const deletedMsgId = payload.old?.id;
        if (deletedMsgId) {
          setMessages(prev => prev.filter(msg => msg.id !== deletedMsgId));
        }
      }
    });

    return () => {
      subscription?.unsubscribe?.();
    };
  }, [activeChat]);

  // Mark chat as read
  useEffect(() => {
    if (!activeChat || !userId) return;

    const markAsRead = async () => {
      await messagingService.markChatAsRead(activeChat, userId);
    };

    markAsRead();

    return () => {
      // Update last read when leaving
      markAsRead();
    };
  }, [activeChat, userId]);

  const handleSendMessage = async () => {
    if (!activeChat || !newMessage.trim()) return;

    const messageId = uuidv4();
    const timestamp = new Date().toISOString();

    // Check for URLs to create link previews
    let linkPreview = undefined;
    const urls = extractUrlsFromText(newMessage);
    if (urls.length > 0) {
      try {
        linkPreview = await messagingService.fetchLinkPreview(urls[0]);
      } catch (error) {
        console.error('Error getting link preview:', error);
      }
    }

    // Send message
    try {
      await messagingService.sendMessage(
        activeChat,
        userId,
        newMessage,
        linkPreview || undefined
      );

      setNewMessage('');
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  const handleImageUpload = async (file: File) => {
    if (!activeChat) return;

    try {
      // Resize image before upload
      const resizedImage = await resizeImageClient(file, 1200, 300, [1, 1.91]);

      // Create a temporary URL for immediate display
      const tempUrl = URL.createObjectURL(resizedImage);

      // Add a temporary message with the image
      const tempId = `temp-${uuidv4()}`;
      const tempMessage: Message = {
        id: tempId,
        sender: userId,
        text: '',
        timestamp: new Date().toISOString(),
        imageUrl: tempUrl,
        reactions: {}
      };

      setMessages(prev => [...prev, tempMessage]);

      setTimeout(() => {
        setMessages(prev => prev.filter(msg => msg.id !== tempId));
        messagingService.sendMessage(
          activeChat,
          userId,
          '',
          undefined,
          tempUrl
        );
      }, 1000);
    } catch (error) {
      console.error('Error uploading image:', error);
    }
  };

  const handleDeleteMessage = async (messageId: string) => {
    if (!activeChat) return;

    try {
      await messagingService.deleteMessage(messageId, activeChat);
    } catch (error) {
      console.error('Error deleting message:', error);
    }
  };

  const handleAddReaction = async (messageId: string, emoji: string) => {
    if (!activeChat) return;

    try {
      await messagingService.addReaction(messageId, userId, emoji);
    } catch (error) {
      console.error('Error adding reaction:', error);
    }
  };

  const handleDeleteChat = async () => {
    if (!activeChat) return;

    try {
      await messagingService.deleteChat(activeChat);
    } catch (error) {
      console.error('Error deleting chat:', error);
    }
  };

  return (
    <ChatViewModular
      className={className}
      userId={userId}
      activeChat={activeChat}
      messages={messages}
      chatDetails={chatDetails}
      participants={participants}
      newMessage={newMessage}
      setNewMessage={setNewMessage}
      onSendMessage={handleSendMessage}
      onImageUpload={handleImageUpload}
      onDeleteMessage={handleDeleteMessage}
      onAddReaction={handleAddReaction}
      onDeleteChat={handleDeleteChat}
      onBackButton={onBackButton}
    />
  );
}
